import React from 'react';
import { Box, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { cardSx, colors, typography } from '../constants';

/**
 * Renderer for the block descriptions the Insights topics produce.
 *
 * Every answer is a list of blocks rather than bespoke markup, which is what
 * lets twenty questions share one presentation layer: a topic builder decides
 * what to say, this decides how it looks.
 */

function toneColor(tone) {
  switch (tone) {
    case 'good':
      return colors.success;
    case 'bad':
      return colors.destructive;
    case 'warn':
      return colors.warning;
    case 'accent':
      return colors.primary;
    default:
      return colors.foreground;
  }
}

function toneSurface(tone) {
  switch (tone) {
    case 'good':
      return `${colors.success}1A`;
    case 'bad':
      return `${colors.destructive}1A`;
    case 'warn':
      return `${colors.warning}1A`;
    case 'accent':
      return `${colors.primary}14`;
    default:
      return colors.muted;
  }
}

const blockCardSx = { ...cardSx, p: 1.75, mb: 1.5 };

function SectionTitle({ text }) {
  return (
    <Typography
      sx={{
        fontSize: typography.caption,
        fontWeight: 700,
        color: colors.foreground,
        mb: 1.5,
      }}
    >
      {text}
    </Typography>
  );
}

function StatCards({ cards }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
      {cards.map((c, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            minWidth: 150,
            backgroundColor: colors.card,
            borderRadius: 3,
            py: 1.75,
            px: 1.25,
            border: c.highlight
              ? `1.5px solid ${toneColor(c.tone ?? 'good')}`
              : `1px solid ${colors.border}`,
          }}
        >
          <Typography
            sx={{
              fontSize: typography.caption,
              fontWeight: 700,
              color: colors.faint,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              mb: 0.75,
            }}
          >
            {c.label}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: typography.heading,
              fontWeight: 800,
              color: toneColor(c.tone),
            }}
          >
            {c.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Bars({ title, rows }) {
  return (
    <Box sx={blockCardSx}>
      {title ? <SectionTitle text={title} /> : null}
      {rows.map((r, i) => (
        <Box key={i} sx={{ mb: i === rows.length - 1 ? 0 : 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.625,
              gap: 1,
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: typography.caption, color: colors.foreground }}
            >
              {r.label}
            </Typography>
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 700,
                color: colors.foreground,
                flexShrink: 0,
              }}
            >
              {r.value}
            </Typography>
          </Box>
          <Box
            sx={{
              height: 8,
              backgroundColor: colors.muted,
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                borderRadius: 999,
                // A sliver of bar is still drawn at zero so the row reads as a
                // measured value rather than a rendering gap.
                width: `${Math.min(100, Math.max(2, r.pct))}%`,
                backgroundColor:
                  r.tone === 'neutral' || !r.tone
                    ? colors.faint
                    : toneColor(r.tone),
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function Compare({ title, items }) {
  return (
    <Box sx={blockCardSx}>
      {title ? <SectionTitle text={title} /> : null}
      <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              minWidth: 150,
              backgroundColor: toneSurface(it.tone),
              borderRadius: 2.5,
              py: 2,
              px: 1.25,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 600,
                color: colors.foreground,
                mb: 0.75,
              }}
            >
              {it.title}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontSize: typography.display,
                fontWeight: 800,
                color: toneColor(it.tone),
              }}
            >
              {it.value}
            </Typography>
            {it.sub ? (
              <Typography
                sx={{
                  fontSize: typography.caption,
                  mt: 0.5,
                  color:
                    it.tone && it.tone !== 'neutral'
                      ? `${toneColor(it.tone)}CC`
                      : colors.faint,
                }}
              >
                {it.sub}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Verdict({ title, items }) {
  return (
    <Box sx={blockCardSx}>
      {title ? <SectionTitle text={title} /> : null}
      <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              minWidth: 150,
              backgroundColor: it.ok
                ? `${colors.success}14`
                : `${colors.destructive}12`,
              borderRadius: 2.5,
              py: 2,
              px: 1.25,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 600,
                color: colors.foreground,
                mb: 1,
              }}
            >
              {it.label}
            </Typography>
            {it.ok ? (
              <CheckIcon sx={{ fontSize: 28, color: colors.success }} />
            ) : (
              <CloseIcon sx={{ fontSize: 28, color: colors.destructive }} />
            )}
            {it.sub ? (
              <Typography
                sx={{
                  fontSize: typography.caption,
                  color: colors.mutedForeground,
                  mt: 1,
                }}
              >
                {it.sub}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Tips({ items }) {
  return (
    <Box sx={blockCardSx}>
      {items.map((t, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
            mb: i === items.length - 1 ? 0 : 1.5,
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: `${colors.primary}1A`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: '1px',
            }}
          >
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 800,
                color: colors.primary,
              }}
            >
              {i + 1}
            </Typography>
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: typography.caption,
              lineHeight: 1.65,
              color: colors.foreground,
            }}
          >
            {t}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Takeaway({ text, color }) {
  const tint = color ?? colors.success;
  return (
    <Box
      sx={{
        backgroundColor: `${tint}16`,
        borderRadius: 3,
        p: 1.75,
        mb: 1.5,
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          flexShrink: 0,
          backgroundColor: `${tint}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BoltIcon sx={{ fontSize: 16, color: tint }} />
      </Box>
      <Typography
        sx={{
          flex: 1,
          fontSize: typography.small,
          lineHeight: 1.6,
          fontWeight: 600,
          color: colors.foreground,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

export default function InsightBlocks({ blocks, accentColor }) {
  return (
    <Box>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'stats':
            return <StatCards key={i} cards={b.cards} />;
          case 'bars':
            return <Bars key={i} title={b.title} rows={b.rows} />;
          case 'compare':
            return <Compare key={i} title={b.title} items={b.items} />;
          case 'verdict':
            return <Verdict key={i} title={b.title} items={b.items} />;
          case 'tips':
            return <Tips key={i} items={b.items} />;
          case 'takeaway':
            return (
              <Takeaway key={i} text={b.text} color={b.color ?? accentColor} />
            );
          default:
            return null;
        }
      })}
    </Box>
  );
}
